import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ClockIcon, CheckCircleIcon, TrophyIcon, DownloadIcon } from '../icons/InterfaceIcons';
import { generatePDFFromTestData } from '../../utils/pdfGenerator';
import { useApp } from '../../contexts/AppContext';

interface TestHistoryCardProps {
  completedTests: any;
}

export const TestHistoryCard: React.FC<TestHistoryCardProps> = ({ completedTests }) => {
  const navigate = useNavigate();
  const { user } = useApp();
  
  const recentTests = completedTests ? Object.values(completedTests).slice(-5) : [];

  const getScoreColor = (score: number, totalMarks: number) => {
    const percentage = (score / totalMarks) * 100;
    if (percentage >= 80) return 'text-green-600';
    if (percentage >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div className="bg-brand-surface border border-brand-border rounded-xl p-6 shadow-card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-xl text-brand-text-primary">Recent Tests</h3>
        <TrophyIcon className="w-6 h-6 text-brand-text-secondary" />
      </div>
      
      {recentTests.length > 0 ? (
        <div className="space-y-3">
          {recentTests.map((test: any, index: number) => (
            <div key={index} className="flex items-center justify-between p-3 bg-brand-bg rounded-lg hover:bg-brand-border/20 transition-colors cursor-pointer">
              <div className="flex-1">
                <h4 className="font-semibold text-brand-text-primary text-sm">{test.name}</h4>
                <div className="flex items-center space-x-4 text-xs text-brand-text-secondary mt-1">
                  <div className="flex items-center space-x-1">
                    <CheckCircleIcon className="w-3 h-3" />
                    <span>{test.accuracy}% accuracy</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <ClockIcon className="w-3 h-3" />
                    <span>{Math.floor(test.time_taken / 60)}m</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <div className="text-right">
                  <div className={`font-bold ${getScoreColor(test.score, test.totalMarks || test.no_q * 4)}`}>
                    {test.score}/{test.totalMarks || test.no_q * 4}
                  </div>
                  <div className="text-xs text-brand-text-secondary">
                    {new Date(test.attempted_at).toLocaleDateString()}
                  </div>
                </div>
                <div className="flex flex-col space-y-1">
                  <button
                    onClick={() => generatePDFFromTestData(test, 'analysis')}
                    className="p-1 text-brand-primary hover:bg-brand-primary/10 rounded cursor-pointer"
                    title="Download Analysis"
                  >
                    <DownloadIcon className="w-3 h-3" />
                  </button>
                  <button
                    onClick={() => generatePDFFromTestData(test, 'certificate', user)}
                    className="p-1 text-green-600 hover:bg-green-100 dark:hover:bg-green-900/30 rounded cursor-pointer"
                    title="Download Certificate"
                  >
                    <TrophyIcon className="w-3 h-3" />
                  </button>
                </div>
              </div>
            </div>
          ))}
          
          <button 
            onClick={() => navigate('/test-history')}
            className="w-full mt-4 py-2 text-sm font-semibold text-brand-primary hover:bg-brand-primary/10 rounded-lg transition-colors cursor-pointer"
          >
            View All Tests
          </button>
        </div>
      ) : (
        <div className="text-center py-8">
          <p className="text-brand-text-secondary text-sm">No tests completed yet</p>
          <p className="text-brand-text-secondary text-xs mt-1">Start practicing to see your progress here</p>
        </div>
      )}
    </div>
  );
};